import React from 'react';
import useWindowSize from 'lib/use-window-resize';
import PageSection from 'components/page/page-section';
import { Columns, ColumnsItem } from 'components/columns';
import Cards, { CardsTypes } from 'components/cards/cards';
import { Header1 } from 'components/typography';
import HelplineNavigation from 'components/helpline/helpline-navigation';
import HelplineCard, { HelpLineItem } from 'components/helpline/helpline-card';
import 'resize-observer-polyfill/dist/ResizeObserver.global';
import lexemes from 'components/lexemes';
import {
    useResizeObserver,
    useDebouncedCallback,
    UseResizeObserverCallback,
} from '@react-hookz/web';
import Image from 'next/image';

export type HelplineSectionCardType =
    | {
          title?: string;
          imageUrl?: string;
          items?: HelpLineItem[] | undefined | null;
      }
    | undefined
    | null;

export type HelplineSectionProps = {
    cards: HelplineSectionCardType[] | undefined | null;
};

const RESIZE_DEBOUNCE = 500;

const getScrollGap = (el: HTMLDivElement | null, width?: number) => {
    return width && el
        ? width + parseInt(window.getComputedStyle(el).getPropertyValue('gap'), 10)
        : 0;
};

const HelplineSection: React.FC<HelplineSectionProps> = ({ cards }) => {
    const cardsWrapperRef = React.useRef<HTMLDivElement>(null);
    const cardsRef = React.useRef<HTMLDivElement>(null);
    const cardRef = React.useRef<HTMLDivElement>(null);
    const scrollDistance = React.useRef(0);
    const [hasPrevScrollSpace, setPrevScrollSpace] = React.useState(false);
    const [hasNextScrollSpace, setNextScrollSpace] = React.useState(true);
    const [scrollDirection, setScrollDirection] = React.useState<1 | -1>(1);
    const [hasCardsNavigation, setCardsNavigation] = React.useState(false);

    const [rect, setRect] = React.useState<DOMRectReadOnly>();
    const resizeCallback = useDebouncedCallback(
        ((event) => setRect(event.contentRect)) as UseResizeObserverCallback,
        [setRect],
        RESIZE_DEBOUNCE
    );

    useResizeObserver(cardsWrapperRef, resizeCallback);

    React.useEffect(() => {
        scrollDistance.current = getScrollGap(cardsRef.current, cardRef.current?.offsetWidth);
        cardsWrapperRef?.current?.scrollTo(0, 0);
    }, [rect]);

    const onClickScrollMovementHandler = (direction: 1 | -1) => {
        if (direction) {
            cardsWrapperRef?.current?.scrollBy(direction * scrollDistance.current, 0);
        }
    };
    const onNextClickHandler = () => onClickScrollMovementHandler(1);
    const onPrevClickHandler = () => onClickScrollMovementHandler(-1);
    const onScrollHandler = useDebouncedCallback(
        (event: React.SyntheticEvent<HTMLElement>) => {
            if (event.target) {
                const { scrollLeft, scrollWidth, offsetWidth } = event.target as HTMLElement;
                const scrollMinBoundary = 10;

                if (scrollLeft < scrollMinBoundary) {
                    setScrollDirection(1);
                    setPrevScrollSpace(false);
                } else {
                    setPrevScrollSpace(true);
                }
                if (scrollWidth - (scrollLeft + offsetWidth) < scrollMinBoundary) {
                    setScrollDirection(-1);
                    setNextScrollSpace(false);
                } else {
                    setNextScrollSpace(true);
                }
            }
        },
        [],
        200
    );

    const [mobileMode, setMobileMode] = React.useState<boolean | undefined>(undefined);

    const size = useWindowSize();

    React.useEffect(() => {
        if (
            cardsWrapperRef.current?.clientWidth &&
            cardsRef.current?.clientWidth &&
            cardsWrapperRef.current?.clientWidth < cardsRef.current?.clientWidth
        ) {
            setCardsNavigation(true);
        } else {
            setCardsNavigation(false);
        }
    }, [cardsWrapperRef, cardsRef]);

    React.useEffect(() => {
        setMobileMode(Boolean(size?.width && size.width < 768));
    }, [size]);

    return (
        <>
            <PageSection theme={PageSection.Theme.ONE}>
                <Columns vAlignTop={true} isMobileFlexible={mobileMode} fluid={true}>
                    <ColumnsItem size={ColumnsItem.size.fluid}>
                        <Header1
                            style={{
                                color: 'var(--color-primary-02)',
                                marginTop: '-4%',
                            }}
                            tag="h2"
                            id="helplines"
                        >
                            {lexemes.helpLines.lxBlockTitle()}
                        </Header1>
                    </ColumnsItem>
                    <ColumnsItem size={ColumnsItem.size.fluid} end={true}>
                        {hasCardsNavigation ? (
                            <HelplineNavigation
                                onNextClickHandler={
                                    hasNextScrollSpace ? onNextClickHandler : undefined
                                }
                                onPrevClickHandler={
                                    hasPrevScrollSpace ? onPrevClickHandler : undefined
                                }
                                scrollDirection={scrollDirection}
                            />
                        ) : null}
                    </ColumnsItem>
                </Columns>
            </PageSection>
            <PageSection
                theme={PageSection.Theme.ONE}
                noTopPadding={true}
                noHorizontalPadding={mobileMode}
            >
                <Columns vAlignTop={true}>
                    <ColumnsItem size={ColumnsItem.size.fluid}>
                        <div
                            className="helpline-section"
                            ref={cardsWrapperRef}
                            onScroll={onScrollHandler}
                            tabIndex={0}
                            role="group"
                        >
                            <Cards type={CardsTypes.HELPLINE} ref={cardsRef}>
                                <>
                                    {cards &&
                                        cards.map((card, index) => {
                                            if (!card) {
                                                return <></>;
                                            }

                                            const { title, imageUrl, items } = card;
                                            return (
                                                <React.Fragment key={index}>
                                                    <div
                                                        className="helpline-card-wrapper"
                                                        ref={index === 0 ? cardRef : null}
                                                    >
                                                        <HelplineCard
                                                            title={title}
                                                            icon={
                                                                imageUrl ? (
                                                                    <div className="helpline-section__image">
                                                                        <Image
                                                                            src={imageUrl}
                                                                            alt=""
                                                                            layout="responsive"
                                                                            // width="100%"
                                                                            // height="100%"
                                                                            objectFit="contain"
                                                                        />
                                                                    </div>
                                                                ) : null
                                                            }
                                                            items={items}
                                                        />
                                                    </div>
                                                </React.Fragment>
                                            );
                                        })}
                                </>
                            </Cards>
                        </div>
                    </ColumnsItem>
                </Columns>
            </PageSection>
        </>
    );
};

export default HelplineSection;
